import { loadReportOneDSEvent } from '@ms/yammer-telemetry-1ds';
import {
  bufferPreConfigEvent,
  getTelemetryClientConfig,
  isTelemetryClientConfigConfigured,
} from '@ms/yammer-telemetry-store';

import type { AnalyticsEvent, AnalyticsV2Event, LogEvent, PerformanceEvent, TelemetryEvent } from '../events';
import { isAnalyticsEvent, isAnalyticsV2Event, isLogEvent } from '../events';
import { applyMiddleware } from '../middleware';

import { reportAnalyticsEvent, reportAnalyticsEventNow } from './reportAnalytics';
import { reportAnalyticsV2Event } from './reportAnalyticsV2';
import { enqueueAndThrottleReportLogEvents, reportLogEvent, reportLogEventNow } from './reportLog';

export type OneDSEventPayload = Parameters<Awaited<ReturnType<typeof loadReportOneDSEvent>>['reportOneDSEvent']>[0];

const isOneDSDestination = (): boolean => {
  const config = getTelemetryClientConfig();

  return !!config?.oneDSInstrumentationKey;
};

const sendTo1DS = async (payload: OneDSEventPayload): Promise<void> => {
  const { reportOneDSEvent } = await loadReportOneDSEvent();
  reportOneDSEvent(payload);
};

const sendTo1DSIfConfigured = async (oneDSPayload?: OneDSEventPayload): Promise<boolean> => {
  if (oneDSPayload && isOneDSDestination()) {
    await sendTo1DS(oneDSPayload);

    return true;
  }

  return false;
};

const bufferEventIfTelemetryIsNotConfigured = (event: TelemetryEvent): boolean => {
  if (!isTelemetryClientConfigConfigured()) {
    bufferPreConfigEvent(event);

    return true;
  }

  return false;
};

const emitToLegacy = async (event: TelemetryEvent): Promise<void> => {
  const processedEvent = await applyMiddleware(event);

  if (isAnalyticsEvent(processedEvent)) {
    reportAnalyticsEvent(processedEvent);
  } else if (isAnalyticsV2Event(processedEvent)) {
    reportAnalyticsV2Event(processedEvent);
  } else {
    reportLogEvent(processedEvent);
  }
};

const emitToLegacyNow = async (event: AnalyticsEvent | LogEvent): Promise<void> => {
  const processedEvent = await applyMiddleware(event);

  if (isAnalyticsEvent(processedEvent)) {
    await reportAnalyticsEventNow(processedEvent);
  } else if (isLogEvent(processedEvent)) {
    await reportLogEventNow(processedEvent);
  }
};

type GetAnalyticsV2LegacyEvent = () => Promise<AnalyticsV2Event | undefined>;
type RouteAnalyticsV2TelemetryEventToLegacy = (getLegacyEvent: GetAnalyticsV2LegacyEvent) => Promise<void>;
const routeAnalyticsV2TelemetryEventToLegacy: RouteAnalyticsV2TelemetryEventToLegacy = async (getLegacyEvent) => {
  const legacyEvent = await getLegacyEvent();
  if (legacyEvent) {
    await emitTelemetryEvent(legacyEvent);
  }
};

export type RoutePerformanceBatch = (events: PerformanceEvent[]) => Promise<void>;
export const routePerformanceBatch: RoutePerformanceBatch = (events) =>
  enqueueAndThrottleReportLogEvents('Performance', events);

export type EmitTelemetryEvent = (event: TelemetryEvent, oneDSPayload?: OneDSEventPayload) => Promise<void>;
export const emitTelemetryEvent: EmitTelemetryEvent = async (event, oneDSPayload) => {
  if (await sendTo1DSIfConfigured(oneDSPayload)) {
    return;
  }

  if (bufferEventIfTelemetryIsNotConfigured(event)) {
    return;
  }

  await emitToLegacy(event);
};

export type EmitTelemetryEventNow = (
  event: AnalyticsEvent | LogEvent,
  oneDSPayload?: OneDSEventPayload
) => Promise<void>;
export const emitTelemetryEventNow: EmitTelemetryEventNow = async (event, oneDSPayload) => {
  if (await sendTo1DSIfConfigured(oneDSPayload)) {
    return;
  }

  if (bufferEventIfTelemetryIsNotConfigured(event)) {
    return;
  }

  await emitToLegacyNow(event);
};

export interface EmitAnalyticsV2TelemetryEventOptions {
  readonly oneDSPayload: OneDSEventPayload;
  readonly getLegacyEvent: GetAnalyticsV2LegacyEvent;
}

export type EmitAnalyticsV2TelemetryEvent = (options: EmitAnalyticsV2TelemetryEventOptions) => Promise<void>;
export const emitAnalyticsV2TelemetryEvent: EmitAnalyticsV2TelemetryEvent = async ({
  oneDSPayload,
  getLegacyEvent,
}) => {
  if (await sendTo1DSIfConfigured(oneDSPayload)) {
    return;
  }

  await routeAnalyticsV2TelemetryEventToLegacy(getLegacyEvent);
};
